import { DialogController } from 'aurelia-dialog';
import { autoinject } from 'aurelia-framework';
import { ConfirmDialog } from 'components/dialogs/confirm/confirm-dialog';
import { AsyncErrorHandler } from 'lib/ui';
import { MaterialFactorUpdateDto } from 'models/mooring/MaterialFactor';
import { MaterialFactorService } from 'services/material-factor-service';

type MaterialFactorType =
  | 'RopeFactor'
  | 'SinkRopeFactor'
  | 'StrapFactor'
  | 'RopeWithKnotFactor'
  | 'ChainFactor'
  | 'ConnectingElementSteelFactor'
  | 'SteelWireRopeFactor'
  | 'ShackleFactor'
  | 'MountainBoltAndDragAnchorFactor';

@autoinject
export class MaterialFactorsCreateEditDialog {
  protected id: number;
  protected materialFactor: MaterialFactorUpdateDto = new MaterialFactorUpdateDto();
  protected ready = false;

  constructor(
    private dialogController: DialogController,
    private materialFactorService: MaterialFactorService,
    private confirmation: ConfirmDialog
  ) {}

  async activate(params: { id: number }) {
    if (!params) return;
    this.id = params.id;
    await this.getMaterialFactor(this.id);
  }

  @AsyncErrorHandler
  async getMaterialFactor(id: number) {
    const data = await this.materialFactorService.get(id);

    this.materialFactor.Id = data.Id;
    this.materialFactor.RopeFactor = data.RopeFactor;
    this.materialFactor.RopeAccidentFactor = Number((data.RopeFactor / 1.5).toFixed(2));
    this.materialFactor.SinkRopeFactor = data.SinkRopeFactor;
    this.materialFactor.SinkRopeAccidentFactor = Number((data.SinkRopeFactor / 1.5).toFixed(2));
    this.materialFactor.StrapFactor = data.StrapFactor;
    this.materialFactor.StrapAccidentFactor = Number((data.StrapFactor / 1.5).toFixed(2));
    this.materialFactor.RopeWithKnotFactor = data.RopeWithKnotFactor;
    this.materialFactor.RopeWithKnotAccidentFactor = Number((data.RopeWithKnotFactor / 1.5).toFixed(2));
    this.materialFactor.ChainFactor = data.ChainFactor;
    this.materialFactor.ChainAccidentFactor = Number((data.ChainFactor / 1.5).toFixed(2));
    this.materialFactor.ConnectingElementSteelFactor = data.ConnectingElementSteelFactor;
    this.materialFactor.ConnectingElementSteelAccidentFactor = Number((data.ConnectingElementSteelFactor / 1.5).toFixed(2));
    this.materialFactor.SteelWireRopeFactor = data.SteelWireRopeFactor;
    this.materialFactor.SteelWireRopeAccidentFactor = Number((data.SteelWireRopeFactor / 1.5).toFixed(2));
    this.materialFactor.ShackleFactor = data.ShackleFactor;
    this.materialFactor.ShackleAccidentFactor = Number((data.ShackleFactor / 1.5).toFixed(2));
    this.materialFactor.MountainBoltAndDragAnchorFactor = data.MountainBoltAndDragAnchorFactor;
    this.materialFactor.MountainBoltAndDragAnchorAccidentFactor = Number((data.MountainBoltAndDragAnchorFactor / 1.5).toFixed(2));
    this.ready = true;
  }

  @AsyncErrorHandler
  async save() {
    const approved = await this.confirmation.confirmYesCancel('dialog.areYouSureContinue', 'dialog.materialFactorApproveText');

    if (approved) {
      await this.materialFactorService.updateAndCreate(this.materialFactor.Id, this.materialFactor);
      this.dialogController.cancel();
    }
  }

  private calculateAccidentFactor(newFactor: number, factorType: MaterialFactorType) {
    switch (factorType) {
      case 'RopeFactor':
        this.materialFactor.RopeAccidentFactor = Number((newFactor / 1.5).toFixed(2));
        break;
      case 'SinkRopeFactor':
        this.materialFactor.SinkRopeAccidentFactor = Number((newFactor / 1.5).toFixed(2));
        break;
      case 'StrapFactor':
        this.materialFactor.StrapAccidentFactor = Number((newFactor / 1.5).toFixed(2));
        break;
      case 'RopeWithKnotFactor':
        this.materialFactor.RopeWithKnotAccidentFactor = Number((newFactor / 1.5).toFixed(2));
        break;
      case 'ChainFactor':
        this.materialFactor.ChainAccidentFactor = Number((newFactor / 1.5).toFixed(2));
        break;
      case 'ConnectingElementSteelFactor':
        this.materialFactor.ConnectingElementSteelAccidentFactor = Number((newFactor / 1.5).toFixed(2));
        break;
      case 'SteelWireRopeFactor':
        this.materialFactor.SteelWireRopeAccidentFactor = Number((newFactor / 1.5).toFixed(2));
        break;
      case 'ShackleFactor':
        this.materialFactor.ShackleAccidentFactor = Number((newFactor / 1.5).toFixed(2));
        break;
      case 'MountainBoltAndDragAnchorFactor':
        this.materialFactor.MountainBoltAndDragAnchorAccidentFactor = Number((newFactor / 1.5).toFixed(2));
        break;
      default:
        break;
    }
  }
}
